import { BRAND } from '../config/branding';
import { LEVELS } from '../data/levels';
import { flavorFromLegacyPalette, getFlavor } from '../data/flavors';
import type { Difficulty, FlavorId, Rank, RunResult } from './types';

export interface ControlBindings {
  up: string;
  down: string;
  left: string;
  right: string;
  wall: string;
  shatter: string;
  special: string;
  pause: string;
  restart: string;
}

export interface SettingsData {
  musicVolume: number;
  sfxVolume: number;
  reducedMotion: boolean;
  shakeStrength: number;
  difficulty: Difficulty;
  highContrast: boolean;
  controls: ControlBindings;
}

export interface LevelProgress {
  bestTimeMs: number | null;
  bestRank: Rank | null;
  bestScore: number;
  medals: { speed: boolean; noDamage: boolean; shard: boolean };
}

export interface SaveData {
  version: number;
  unlockedLevel: number;
  selectedFlavor: FlavorId;
  selectedSecondFlavor: FlavorId;
  unlockedToppings: string[];
  settings: SettingsData;
  levels: Record<string, LevelProgress>;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const DEFAULT_CONTROLS: ControlBindings = {
  up: 'KeyW',
  down: 'KeyS',
  left: 'KeyA',
  right: 'KeyD',
  wall: 'Space',
  shatter: 'KeyE',
  special: 'ShiftLeft',
  pause: 'Escape',
  restart: 'KeyR',
};

export function createDefaultSave(): SaveData {
  return {
    version: BRAND.version,
    unlockedLevel: 1,
    selectedFlavor: 'vanilla',
    selectedSecondFlavor: 'strawberry',
    unlockedToppings: [],
    settings: {
      musicVolume: 0.38,
      sfxVolume: 0.72,
      reducedMotion: false,
      shakeStrength: 0.55,
      difficulty: 'standard',
      highContrast: false,
      controls: { ...DEFAULT_CONTROLS },
    },
    levels: {},
  };
}

export function migrateSave(value: unknown): SaveData {
  const defaults = createDefaultSave();
  if (!value || typeof value !== 'object') return defaults;
  const source = value as Partial<SaveData> & {
    settings?: Partial<SettingsData>;
    selectedCharacter?: 'mox' | 'vela';
    selectedPalette?: number;
    unlockedPalettes?: number[];
  };
  const legacyFlavor = typeof source.selectedPalette === 'number'
    ? flavorFromLegacyPalette(source.selectedPalette)
    : source.selectedCharacter === 'vela' ? 'strawberry' : 'vanilla';
  const selectedFlavor = source.selectedFlavor && getFlavor(source.selectedFlavor).id === source.selectedFlavor
    ? source.selectedFlavor
    : legacyFlavor;
  const controls = { ...DEFAULT_CONTROLS, ...source.settings?.controls };
  if (Number(source.version ?? 0) < 3 && (!source.settings?.controls?.wall || source.settings.controls.wall === 'KeyQ')) {
    controls.wall = 'Space';
  }
  return {
    ...defaults,
    ...source,
    version: BRAND.version,
    unlockedLevel: Math.max(1, Math.min(LEVELS.length, Number(source.unlockedLevel ?? 1))),
    selectedFlavor,
    selectedSecondFlavor: source.selectedSecondFlavor && getFlavor(source.selectedSecondFlavor).id === source.selectedSecondFlavor
      ? source.selectedSecondFlavor
      : selectedFlavor === 'strawberry' ? 'vanilla' : 'strawberry',
    unlockedToppings: Array.isArray(source.unlockedToppings) ? source.unlockedToppings.filter((item): item is string => typeof item === 'string') : [],
    settings: {
      ...defaults.settings,
      ...source.settings,
      controls,
    },
    levels: source.levels && typeof source.levels === 'object' ? source.levels : {},
  };
}

export class SaveStore {
  data: SaveData;
  private readonly storage: StorageLike | null;

  constructor(storage: StorageLike | null) {
    this.storage = storage;
    this.data = this.load();
  }

  private load(): SaveData {
    if (!this.storage) return createDefaultSave();
    try {
      const raw = this.storage.getItem(BRAND.storageKey)
        ?? BRAND.legacyStorageKeys.map((key) => this.storage?.getItem(key) ?? null).find((value) => value !== null)
        ?? null;
      return raw ? migrateSave(JSON.parse(raw) as unknown) : createDefaultSave();
    } catch {
      return createDefaultSave();
    }
  }

  persist(): void {
    try {
      this.storage?.setItem(BRAND.storageKey, JSON.stringify(this.data));
    } catch {
      // Storage can be unavailable in privacy mode; the current session remains playable.
    }
  }

  recordResult(result: RunResult): void {
    const key = String(result.levelId);
    const previous = this.data.levels[key] ?? {
      bestTimeMs: null,
      bestRank: null,
      bestScore: 0,
      medals: { speed: false, noDamage: false, shard: false },
    };
    const order: readonly Rank[] = ['C', 'B', 'A', 'S'];
    this.data.levels[key] = {
      bestTimeMs: previous.bestTimeMs === null ? result.timeMs : Math.min(previous.bestTimeMs, result.timeMs),
      bestRank: previous.bestRank === null || order.indexOf(result.rank) > order.indexOf(previous.bestRank) ? result.rank : previous.bestRank,
      bestScore: Math.max(previous.bestScore, result.score),
      medals: {
        speed: previous.medals.speed || result.speedMedal,
        noDamage: previous.medals.noDamage || result.noDamageMedal,
        shard: previous.medals.shard || result.shardMedal,
      },
    };
    this.data.unlockedLevel = Math.max(this.data.unlockedLevel, Math.min(LEVELS.length, result.levelId + 1));
    const topping = `frost-crown-${result.levelId}`;
    if (result.rank === 'S' && !this.data.unlockedToppings.includes(topping)) {
      this.data.unlockedToppings.push(topping);
    }
    this.persist();
  }
}
